// M3.4.4 — affix pool + rolling.
//
// Rare item instances pick 1–3 unique entries from this pool at drop time and
// roll a value within each entry's range. Common items have an empty affix
// list. Names of the affixes match the AffixType / AffixSubType taxonomy
// already defined in items.ts (M3.4.1).
//
// M7.2 made the runtime pool swappable via setAffixPool, mirroring the
// items / mobs registry pattern.

import { z } from "zod";
import type {
  Affix,
  AffixDmgSubType,
  AffixResistSubType,
  AffixStatSubType,
  AffixType,
} from "./items.js";

export interface AffixDefinition {
  type: AffixType;
  subType?: AffixStatSubType | AffixDmgSubType | AffixResistSubType;
  // Inclusive integer roll range.
  min: number;
  max: number;
}

// 16 entries covering every (type, subType) combination from PROMPT.md §5.5.
// Each pick on a rare drop is one of these — duplicates are not rolled twice
// on the same instance (rollAffixes enforces uniqueness).
export const BUILTIN_AFFIX_POOL: readonly AffixDefinition[] = [
  // +(1–3) to a stat
  { type: "stat", subType: "str", min: 1, max: 3 },
  { type: "stat", subType: "dex", min: 1, max: 3 },
  { type: "stat", subType: "con", min: 1, max: 3 },
  { type: "stat", subType: "int", min: 1, max: 3 },
  { type: "stat", subType: "fai", min: 1, max: 3 },
  { type: "stat", subType: "rap", min: 1, max: 3 },

  // +(5–10)% to a damage school
  { type: "dmgPct", subType: "melee", min: 5, max: 10 },
  { type: "dmgPct", subType: "ranged", min: 5, max: 10 },
  { type: "dmgPct", subType: "spell", min: 5, max: 10 },

  // +(10–30) flat HP
  { type: "hp", min: 10, max: 30 },

  // +(1–3)% crit
  { type: "critPct", min: 1, max: 3 },

  // +(10–25) max mana (single pool, M8)
  { type: "mana", min: 10, max: 25 },

  // +(2–5)% resistance — physical + 6 magic schools
  { type: "resistPct", subType: "physical", min: 2, max: 5 },
  { type: "resistPct", subType: "divine", min: 2, max: 5 },
  { type: "resistPct", subType: "necromancy", min: 2, max: 5 },
  { type: "resistPct", subType: "water", min: 2, max: 5 },
  { type: "resistPct", subType: "fire", min: 2, max: 5 },
  { type: "resistPct", subType: "air", min: 2, max: 5 },
  { type: "resistPct", subType: "earth", min: 2, max: 5 },
] as const;

// Back-compat alias for the original `AFFIX_POOL` const — existing tests
// (`server/src/sim/loot.test.ts`) iterate it to verify the builtin shape.
// Read paths in production code should call `getAffixPool()` so admin edits
// land. Builtins never change at runtime — they're the factory baseline.
export const AFFIX_POOL: readonly AffixDefinition[] = BUILTIN_AFFIX_POOL;

// =============================================================================
// Runtime registry (M7.2)
// =============================================================================

let runtimeAffixPool: AffixDefinition[] = [...BUILTIN_AFFIX_POOL];

export function getAffixPool(): readonly AffixDefinition[] {
  return runtimeAffixPool;
}

export function setAffixPool(pool: readonly AffixDefinition[]): void {
  runtimeAffixPool = [...pool];
}

export function _resetAffixPoolForTests(): void {
  runtimeAffixPool = [...BUILTIN_AFFIX_POOL];
}

// =============================================================================
// Zod schemas (M7.2)
// =============================================================================

const AffixDefinitionSchema = z
  .object({
    type: z.enum(["stat", "dmgPct", "hp", "critPct", "mana", "resistPct"]),
    subType: z
      .enum([
        "str",
        "dex",
        "con",
        "int",
        "fai",
        "rap",
        "melee",
        "ranged",
        "spell",
        "physical",
        "divine",
        "necromancy",
        "water",
        "fire",
        "air",
        "earth",
      ])
      .optional(),
    min: z.number().int().min(-100).max(1000),
    max: z.number().int().min(-100).max(1000),
  })
  .strict()
  .refine((d) => d.max >= d.min, "max must be ≥ min");

export const AffixPoolSchema = z.array(AffixDefinitionSchema).min(1).max(64);

// Picks `count` unique affix definitions and rolls a value for each.
// `count` is clamped to [1, AFFIX_POOL.length]. Ordering is deterministic for
// a given rng sequence — useful for tests.
export function rollAffixes(count: number, rng: () => number = Math.random): Affix[] {
  // M7.2 — reads the live runtime pool so admin edits propagate without
  // touching loot.ts. Tests on the builtin shape still pass since the
  // runtime pool defaults to BUILTIN_AFFIX_POOL on import.
  const pool = runtimeAffixPool;
  const n = Math.max(1, Math.min(pool.length, Math.floor(count)));
  // Partial Fisher–Yates: shuffle the first n indices in-place.
  const indices = Array.from({ length: pool.length }, (_, i) => i);
  for (let i = 0; i < n; i++) {
    const j = i + Math.floor(rng() * (indices.length - i));
    const tmp = indices[i]!;
    indices[i] = indices[j]!;
    indices[j] = tmp;
  }
  const result: Affix[] = [];
  for (let i = 0; i < n; i++) {
    const def = pool[indices[i]!]!;
    const value = def.min + Math.floor(rng() * (def.max - def.min + 1));
    const affix: Affix = { type: def.type, value };
    if (def.subType !== undefined) affix.subType = def.subType;
    result.push(affix);
  }
  return result;
}

// Human-readable form for tooltips.
export function formatAffix(affix: Affix): string {
  const sign = affix.value >= 0 ? "+" : "";
  switch (affix.type) {
    case "stat":
      return `${sign}${affix.value} ${(affix.subType ?? "").toUpperCase()}`;
    case "dmgPct":
      return `${sign}${affix.value}% ${affix.subType ?? ""} damage`;
    case "hp":
      return `${sign}${affix.value} HP`;
    case "critPct":
      return `${sign}${affix.value}% crit`;
    case "mana":
      // M8 — single pool; subType is ignored.
      return `${sign}${affix.value} max mana`;
    case "resistPct":
      return `${sign}${affix.value}% ${affix.subType ?? ""} resist`;
  }
}
